<script setup lang="ts">
import type { MegamenuNavigationItems } from '~/data/megamenu'
import type { INavLinkItem } from './NavigationLinks.vue'
import { useIsSubdomain } from '~/composables/useSubdomain'

const props = withDefaults(
  defineProps<{
    logoImg?: string
    extraLinkItems?: INavLinkItem[]
  }>(),
  { logoImg: '/_nuxt3-static/logos/logo.svg', extraLinkItems: () => [] }
)

const $localePath = useLocalePath()
const localePath = $localePath
const isSubdomain = useIsSubdomain()
const route = useRoute()
const { t, tm } = useI18n()
const megamenuNavItems = computed<MegamenuNavigationItems>(() => {
  const items = tm('megamenu.navigationItems') as
    | MegamenuNavigationItems
    | undefined
  return Array.isArray(items) ? items : []
})
const isNavSidebarOpen = ref(false)

const { isMobile } = useDevice()
const toggleNavSidebar = () => {
  isNavSidebarOpen.value = !isNavSidebarOpen.value
  document.body.classList.toggle('prevent-scroll')
}

const closeNavSidebar = () => {
  isNavSidebarOpen.value = false
  document.body.classList.remove('prevent-scroll')
}

const isMounted = ref(false)
const isSearchLoaded = ref(false)

onMounted(() => {
  isMounted.value = true
  nextTick(() => {
    setTimeout(() => {
      isSearchLoaded.value = true
    }, 100)
  })
})

// Reset state on route change to handle CLS properly
watch(
  () => route.fullPath,
  () => {
    isSearchLoaded.value = false
    // Small delay to ensure DOM is ready for placeholder
    nextTick(() => {
      setTimeout(() => {
        isSearchLoaded.value = true
      }, 100)
    })
  },
  { immediate: false }
)

const defaultLinks = computed<INavLinkItem[]>(() => {
  const items = megamenuNavItems.value
  const links: INavLinkItem[] = items
    .filter((item) => !isSubdomain.value || item.showOnSub)
    .map((item) => ({
      path: item.path,
      text: item.label,
      ...(item.showOnSub && { showOnSub: true }),
    }))
  if (isSubdomain.value) {
    links.unshift({ path: '/', text: t('general.allEvents'), showOnSub: true })
  }
  if (props.extraLinkItems?.length) {
    links.push(...props.extraLinkItems)
  }
  return links
})

const navLinks = computed(() => defaultLinks.value)

const megamenuDataForNav = computed(() =>
  isSubdomain.value ? undefined : megamenuNavItems.value
)

const routesWhereSearchInNavbarIsHidden = ['index___de-de', 'index___en-us']
const shouldShowFederatedSearchInCurrentRoute = computed(() => {
  const routeName = route.name as string | undefined
  return (
    routeName &&
    !routesWhereSearchInNavbarIsHidden.includes(routeName) &&
    !isSubdomain.value &&
    isMounted.value
  )
})
</script>

<template>
  <div>
    <nav class="bg-white border-b sm:border-b-0 border-gray-200">
      <div
        class="max-w-screen-xl flex flex-wrap items-center justify-between mx-auto p-4 h-[70px]"
      >
        <HamburgerButton @navbar-open="toggleNavSidebar" />
        <NuxtLink :to="localePath('/')" class="app-logo">
          <img
            :src="logoImg"
            class="h-full w-full object-contain"
            alt="konfetti logo"
          />
        </NuxtLink>
        <!-- Federated search and it's placeholder in desktop -->
        <div
          class="hidden sm:block grow flex justify-center md:w-[calc(100%-720px)] h-[38px]"
        >
          <!--          <FederatedSearchLoadingPlaceholder-->
          <!--            v-if="-->
          <!--              !isSearchLoaded &&-->
          <!--              !isSubdomain &&-->
          <!--              route.name &&-->
          <!--              !routesWhereSearchInNavbarIsHidden.includes(route.name as string)-->
          <!--            "-->
          <!--            class="fullwidth hidden sm:flex"-->
          <!--          />-->

          <LazyFederatedSearch
            v-if="
              shouldShowFederatedSearchInCurrentRoute && isMounted && !isMobile
            "
            element-id="navbar-autocomplete-desktop"
            analytics-placement="navbar"
            :full-width="true"
            class="hidden sm:block"
          />
        </div>
        <NuxtLazyHydrate when-idle>
          <NavbarUserMenu />
        </NuxtLazyHydrate>
      </div>
      <!-- Federated search and placeholder in mobile (viewport: hidden on sm+) -->
      <div
        v-if="
          !isSubdomain &&
          route.name &&
          !routesWhereSearchInNavbarIsHidden.includes(route.name as string)
        "
        class="px-1 pb-2 block sm:hidden relative h-11"
      >
        <FederatedSearchLoadingPlaceholder
          v-show="!isSearchLoaded"
          class="fullwidth flex sm:hidden absolute left-0 right-0"
        />
        <FederatedSearch
          v-if="shouldShowFederatedSearchInCurrentRoute && isMobile"
          element-id="navbar-autocomplete-mobile"
          analytics-placement="navbar"
          :full-width="true"
          class="fullwidth block sm:hidden border-none"
        />
      </div>
    </nav>
  </div>
  <NuxtLazyHydrate when-idle>
    <NavigationLinks
      :is-open="isNavSidebarOpen"
      :nav-links="navLinks"
      :megamenu-data="megamenuDataForNav"
      @navbar-close="closeNavSidebar"
    />
  </NuxtLazyHydrate>
  <NuxtLazyHydrate when-idle>
    <CartSidebar />
  </NuxtLazyHydrate>
</template>
<style scoped lang="postcss">
#navbar-autocomplete-desktop,
#navbar-autocomplete-mobile {
  @apply grow;
  @apply mx-4 sm:mx-8;
}
</style>
<style>
body.prevent-scroll {
  overflow: hidden;
}
.app-logo {
  @apply w-[142px] h-[24px];
  @apply flex items-center space-x-3 rtl:space-x-reverse;
  @media (min-width: 372px) {
    @apply ml-2;
  }
  @media (min-width: 400px) {
    @apply ml-10;
  }
  @media (min-width: 768px) {
    @apply ml-0;
  }
}
</style>
